// This file is used to map the fields for the NGL North Carolina Goods and Services Form
import { getNGLFieldNames } from '@/components/documents/Foxit/FormMapping/FieldNameProviders/NGLFieldNameProvider'
import { useCustomItemFields } from '@/components/documents/Foxit/FormMapping/composables/CustomItemFields'
import {
	MultipleItemsMethod,
	useFormFillHelper,
} from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useIdentitySetup } from '@/components/documents/Foxit/FormMapping/composables/IdentityFields'
import { useSupplementalFields } from '@/components/documents/Foxit/FormMapping/composables/SupplementalFields'
import type {
	GroupedTotalConfig,
	SectionTotalConfig,
} from '@/components/documents/Foxit/FormMapping/composables/TotalFields'
import {
	groupedTotal,
	useTotalFields,
} from '@/components/documents/Foxit/FormMapping/composables/TotalFields'
import type { CartItemAndLineItem } from '@/mixins/merchandise'
import type { CustomCartItem } from '@/types'

export default class NGLNCGoodsAndServicesForm {
	private fieldNameProvider = getNGLFieldNames()

	// Standard list of fields for this form.
	// These values point to field names in the fieldNameProvider, as well as functions to get the value for the field via the fieldValueProvider
	private fields: (keyof typeof this.fieldNameProvider)[] = [
		// Identity
		'insuredFullName',
		'funeralHomeName',

		// Service
		'basicServicesPrice',
		'embalmingAndRefrigerationPrice',
		'otherPrepPrice',
		'visitationPrice',
		'funeralMemorialServicePrice',
		'memorialServicePrice',
		'gravesideServicePrice',
		'transferMileRadiusFields',
		'hearseMileRadiusPrice',
		'familyCarLimoPrice',
		'utilityVehicleVanPrice',
		'forwardingRemainsPrice',
		'receivingRemainsPrice',
		'directCremationPrice',
		'directBurialPrice',
		'multilineServiceOtherFieldsWithTotal',
		'packageInfo',
		'transportationOtherPrice',

		// Merchandise
		'casketFields',
		'outerBurialFields',
		'urnFields',
		'multilineMerchandiseOtherFields',

		// Cash Advance
		'obituaryNoticePublicationPrice',
		'cemeteryChargesPrice',
		'deathCertificatesPrice',
		'flowersPrice',
		'nonGuaranteedCremationServicesPrice',
		'multilineCashAdvanceOtherFieldsWithDiscount',

		// Other
		'otherOtherLine1WithDiscountFields',
		'salesTaxPrice',
	]

	private unfilledFields = [
		'funeralRequirementDesc',
		'reasonForEmbalmingDesc',
		'purchaserInitials',
	]
	private sectionTotals: SectionTotalConfig[] = [
		{ section: 'service' },
		{ section: 'merchandise' },
		{ section: 'cashAdvance' },
		{ section: 'cashAdvanceTax', emit: false },
		{ section: 'other' },
	]
	private groupedTotals: GroupedTotalConfig[] = [
		groupedTotal('guaranteed'),
		groupedTotal('funeralWithTax'),
		groupedTotal('cashAdvanceWithTax'),
	]

	// Set up prior to building form filling array.
	constructor(planHolderId: string, cartItems?: CartItemAndLineItem[]) {
		const { identitySetup } = useIdentitySetup()
		const {
			setMultipleItemsMethod,
			setFieldNameProvider,
			setCartItems,
			setIgnoreSupplementalTextList,
			setNonServiceCategories,
			setOtherLineCounts,
		} = useFormFillHelper()

		identitySetup(planHolderId, ['planHolder', 'funeralHome'])
		setIgnoreSupplementalTextList(
			new Set([
				'gCasketModelDesc',
				'gCasketExteriorDesc',
				'gCasketInteriorDesc',
				'gOuterBurialModelDesc',
				'gOuterBurialConstructedOfDesc',
				'gUrnModelDesc',
				'gUrnConstructedOfDesc',
			])
		)

		setMultipleItemsMethod(MultipleItemsMethod.Supplemental)
		setFieldNameProvider(this.fieldNameProvider)
		setNonServiceCategories()
		setOtherLineCounts({
			service: 13,
			merchandise: 13,
			cashAdvance: 5,
		})

		if (cartItems) {
			setCartItems(cartItems)
		}
	}

	// Build form filling array.
	getFormMappings() {
		const { formatFields, mapFields } = useFormFillHelper()
		const { getTotalsFields } = useTotalFields()
		const { getSupplementalFields } = useSupplementalFields()

		const mainFields = mapFields(this.fields)
		const supplementalFields = getSupplementalFields(mainFields)
		const totalFields = getTotalsFields(
			mainFields,
			this.sectionTotals,
			this.groupedTotals
		)

		return formatFields([...mainFields, ...supplementalFields, ...totalFields])
	}

	// Mappings for only the fields a custom item is filled into, so adding one
	// doesn't overwrite what the AFP entered by hand
	getCustomItemFormMappings(customItemId: CustomCartItem['id']) {
		const { getCustomItemFormMappings } = useCustomItemFields()

		return getCustomItemFormMappings(
			this.getFormMappings(),
			this.fields,
			customItemId
		)
	}
}
